import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import API from "./config/API";

function UpdateUserDetails() {
  const { registerId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [formData, setFormData] = useState({
    fullName: "",
    contactNo: "",
    alternativeMobileNo: "",
    emailId: "",
    aadharCardNo: "",
    userStayingType: "",
    floorName: "",
    roomName: "",
    userBedName: "",
    bedStatus: "",
    userBedJoiningDate: "",
    userBedVacateDate: "",
    paymentType: "",
    paymentStatus: "",
    userBedCost: "",
    userPaidAmount: "",
    paymentDateTime: "",
    userRegisterId: 0,
  });

  useEffect(() => {
    fetchUser();
  }, [registerId]);

  const showToast = (type, message) => {
    setToast({ type, message });

    setTimeout(() => {
      setToast(null);
    }, 2500);
  };

  // ================= AUTH HEADERS =================

  const getAuthHeaders = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${user?.jwtToken}`,
    };
  };

  // ================= GET USER =================

  const fetchUser = async () => {
    try {
      setLoading(true);

      // Dynamic API URL
      const GET_USER_URL = `${API.V1}/user/getUserDataByRegisterId?registerId=${registerId}`;

      console.log("GET USER URL:", GET_USER_URL);

      const res = await axios.get(GET_USER_URL, {
        headers: getAuthHeaders(),
      });

      if (res.data?.statusCode === 200) {
        const user = res.data?.data || {};

        setFormData({
          fullName: user.fullName || "",
          contactNo: user.contactNo || "",
          alternativeMobileNo: user.alternativeMobileNo || "",
          emailId: user.emailId || "",
          aadharCardNo: user.aadharCardNo || "",
          userStayingType: user.userStayingType || "",
          floorName: user.floorName || "",
          roomName: user.roomName || "",
          userBedName: user.userBedName || "",
          bedStatus: user.bedStatus || "",
          userBedJoiningDate: user.userBedJoiningDate || "",
          userBedVacateDate: user.userBedVacateDate || "",
          paymentType: user.paymentType || "",
          paymentStatus: user.paymentStatus || "",
          userBedCost: user.userBedCost || "",
          userPaidAmount: user.userPaidAmount || "",
          paymentDateTime: user.paymentDateTime || "",
          userRegisterId: user.userRegisterId || Number(registerId),
        });
      } else {
        showToast("error", res.data?.message || "No user data found");
      }
    } catch (error) {
      console.error("Fetch user error:", error);

      if (error.response?.status === 401) {
        showToast("error", "Session expired. Please login again.");

        localStorage.clear();
        sessionStorage.clear();

        setTimeout(() => {
          navigate("/");
        }, 1500);
      } else {
        showToast("error", "Unable to load user details");
      }
    } finally {
      setLoading(false);
    }
  };

  // ================= HANDLE CHANGE =================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ================= UPDATE USER =================

  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      const payload = {
        fullName: formData.fullName,

        contactNo: formData.contactNo ? Number(formData.contactNo) : 0,

        alternativeMobileNo: formData.alternativeMobileNo
          ? Number(formData.alternativeMobileNo)
          : 0,

        emailId: formData.emailId,

        aadharCardNo: formData.aadharCardNo,

        userStayingType: formData.userStayingType,

        floorName: formData.floorName,

        roomName: formData.roomName,

        userBedName: formData.userBedName,

        bedStatus: formData.bedStatus,

        paymentType: formData.paymentType,

        paymentStatus: formData.paymentStatus,

        userBedCost: formData.userBedCost ? Number(formData.userBedCost) : 0,

        userPaidAmount: formData.userPaidAmount
          ? Number(formData.userPaidAmount)
          : 0,

        paymentDateTime: formData.paymentDateTime,

        userRegisterId: Number(registerId),
      };

      console.log("Update payload:", payload);

      // Dynamic API URL
      const UPDATE_URL = `${API.V1}/user/updateUserDetails`;

      console.log("UPDATE USER URL:", UPDATE_URL);

      const res = await axios.put(UPDATE_URL, payload, {
        headers: getAuthHeaders(),
      });

      if (res.data?.statusCode === 200 || res.status === 200) {
        showToast(
          "success",
          res.data?.message || "User details updated successfully",
        );

        setTimeout(() => {
          navigate("/tenantsAndGuests");
        }, 1500);
      } else {
        showToast(
          "error",
          res.data?.message || "Failed to update user details",
        );
      }
    } catch (error) {
      console.error("Update user error:", error);

      if (error.response?.status === 401) {
        showToast("error", "Session expired. Please login again.");

        localStorage.clear();
        sessionStorage.clear();

        setTimeout(() => {
          navigate("/");
        }, 1500);
      } else {
        showToast(
          "error",
          error.response?.data?.message || "Failed to update user details",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  // ================= LOADING =================

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loader}></div>

        <h2 style={{ color: "#2563eb" }}>Loading User...</h2>
      </div>
    );
  }

  // ================= UI =================

  return (
    <div style={styles.page}>
      {toast && (
        <div
          style={{
            ...styles.toast,
            background: toast.type === "success" ? "#16a34a" : "#dc2626",
          }}
        >
          {toast.message}
        </div>
      )}

      <div style={styles.card}>
        {/* HEADER */}

        <div style={styles.header}>
          <div style={styles.avatar}>👤</div>

          <div>
            <h1 style={styles.title}>Update Tenant / Guest</h1>

            <p style={styles.subTitle}>Manage customer accommodation details</p>
          </div>
        </div>

        <form onSubmit={handleUpdate}>
          {/* ================= PERSONAL INFORMATION ================= */}

          <div style={styles.section}>
            <div style={styles.sectionTitle}>👨 Personal Information</div>

            <div style={styles.grid}>
              <Input
                icon="👤"
                label="Full Name"
                value={formData.fullName}
                disabled
              />

              <Input
                icon="📱"
                label="Mobile Number"
                value={formData.contactNo}
                disabled
              />

              <Input
                icon="✉️"
                label="Email Address"
                value={formData.emailId}
                disabled
              />

              <Input
                icon="📞"
                label="Alternative Mobile"
                name="alternativeMobileNo"
                value={formData.alternativeMobileNo}
                onChange={handleChange}
                type="tel"
              />

              <Input
                icon="🆔"
                label="Aadhar Number"
                value={formData.aadharCardNo}
                disabled
              />

              <Select
                icon="👥"
                label="Staying Type"
                name="userStayingType"
                value={formData.userStayingType}
                onChange={handleChange}
                options={["Guest", "Tenant"]}
              />
            </div>
          </div>

          {/* ================= ACCOMMODATION ================= */}

          <div style={styles.section}>
            <div style={styles.sectionTitle}>🏠 Accommodation Details</div>

            <div style={styles.grid}>
              <Input
                icon="🏢"
                label="Floor Name"
                name="floorName"
                value={formData.floorName}
                onChange={handleChange}
              />

              <Input
                icon="🚪"
                label="Room Name"
                name="roomName"
                value={formData.roomName}
                onChange={handleChange}
              />

              <Input
                icon="🛏️"
                label="Bed Name"
                name="userBedName"
                value={formData.userBedName}
                onChange={handleChange}
              />

              <Select
                icon="📍"
                label="Bed Status"
                name="bedStatus"
                value={formData.bedStatus || ""}
                onChange={handleChange}
                options={[
                  ...(formData.bedStatus &&
                  ![
                    "VACATE NOW",
                    "READY TO VACATE",
                    "CONTINUE FOR NEXT MONTH",
                  ].includes(formData.bedStatus)
                    ? [formData.bedStatus]
                    : []),

                  "VACATE NOW",
                  "READY TO VACATE",
                  "CONTINUE FOR NEXT MONTH",
                ]}
              />

              <Input
                icon="📅"
                label="Joined Date"
                name="userBedJoiningDate"
                value={formData.userBedJoiningDate}
                disabled
              />

              <Input
                icon="📅"
                label="Bed Last Date"
                name="userBedVacateDate"
                value={formData.userBedVacateDate}
                disabled
              />
            </div>
          </div>

          {/* ================= PAYMENT ================= */}

          <div style={styles.section}>
            <div style={styles.sectionTitle}>💳 Payment Information</div>

            <div style={styles.grid}>
              <Select
                icon="💰"
                label="Payment Type"
                name="paymentType"
                value={formData.paymentType || ""}
                onChange={handleChange}
                options={[
                  ...(formData.paymentType ? [formData.paymentType] : []),

                  "DAY",
                  "MONTH",
                  "YEAR",
                ].filter(
                  (value, index, array) => array.indexOf(value) === index,
                )}
              />

              <Input
                icon="✔️"
                label="Payment Status"
                value={formData.paymentStatus}
                disabled
              />

              <Input
                icon="🏷️"
                label="Bed Cost"
                value={formData.userBedCost}
                disabled
              />

              <Input
                icon="💵"
                label="Paid Amount"
                value={formData.userPaidAmount}
                disabled
              />

              <Input
                icon="📅"
                label="Payment Date"
                value={formData.paymentDateTime}
                disabled
              />
            </div>
          </div>

          {/* ================= BUTTONS ================= */}

          <div style={styles.buttonContainer}>
            <button
              type="button"
              style={styles.backBtn}
              onClick={() => navigate("/tenantsAndGuests")}
            >
              ← Back
            </button>

            <button
              type="submit"
              style={{
                ...styles.updateBtn,
                opacity: saving ? 0.7 : 1,
                cursor: saving ? "not-allowed" : "pointer",
              }}
              disabled={saving}
            >
              {saving ? "Updating..." : "💾 Update Details"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UpdateUserDetails;

/* ================= INPUT ================= */

const Input = ({ icon, label, disabled, ...props }) => (
  <div style={styles.inputCard}>
    <label style={styles.label}>
      {icon} {label}
    </label>

    <input
      {...props}
      disabled={disabled}
      style={{
        ...styles.input,
        ...(disabled ? styles.disabledInput : {}),
      }}
    />
  </div>
);

/* ================= SELECT ================= */

const Select = ({ icon, label, options, ...props }) => (
  <div style={styles.inputCard}>
    <label style={styles.label}>
      {icon} {label}
    </label>

    <select {...props} style={styles.input}>
      <option value="">Select</option>

      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  </div>
);

/* ================= STYLES ================= */

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg,#dbeafe,#eff6ff,#ffffff)",
    padding: "40px",
  },

  card: {
    maxWidth: "1100px",
    margin: "auto",
    background: "#fff",
    borderRadius: "20px",
    padding: "35px",
    boxShadow: "0 20px 45px rgba(0,0,0,.12)",
  },

  header: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    marginBottom: "35px",
    borderBottom: "2px solid #e2e8f0",
    paddingBottom: "20px",
  },

  avatar: {
    width: "75px",
    height: "75px",
    borderRadius: "50%",
    background: "linear-gradient(135deg,#2563eb,#3b82f6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "34px",
    color: "#fff",
  },

  title: {
    margin: 0,
    color: "#1e3a8a",
    fontSize: "30px",
    fontWeight: "700",
  },

  subTitle: {
    marginTop: "6px",
    color: "#64748b",
    fontSize: "15px",
  },

  section: {
    marginTop: "35px",
    background: "#f8fbff",
    padding: "25px",
    borderRadius: "18px",
    border: "1px solid #dbeafe",
  },

  sectionTitle: {
    fontSize: "21px",
    fontWeight: "700",
    color: "#1d4ed8",
    marginBottom: "22px",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
    gap: "20px",
  },

  inputCard: {
    background: "#fff",
    padding: "14px",
    borderRadius: "14px",
    border: "1px solid #cbd5e1",
    boxShadow: "0 4px 10px rgba(0,0,0,.05)",
  },

  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: "700",
    color: "#334155",
    fontSize: "14px",
  },

  input: {
    width: "100%",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    outline: "none",
    fontSize: "15px",
    background: "#fff",
    boxSizing: "border-box",
  },

  disabledInput: {
    background: "#edf2f7",
    color: "#475569",
    fontWeight: "600",
  },

  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "35px",
  },

  backBtn: {
    padding: "12px 28px",
    border: "none",
    borderRadius: "10px",
    background: "#64748b",
    color: "#fff",
    fontWeight: "700",
    cursor: "pointer",
    fontSize: "15px",
  },

  updateBtn: {
    padding: "12px 35px",
    border: "none",
    borderRadius: "10px",
    background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
    color: "#fff",
    fontWeight: "700",
    cursor: "pointer",
    fontSize: "16px",
    boxShadow: "0 10px 25px rgba(37,99,235,.35)",
  },

  toast: {
    position: "fixed",
    top: "25px",
    right: "25px",
    color: "#fff",
    padding: "14px 22px",
    borderRadius: "10px",
    fontWeight: "700",
    boxShadow: "0 8px 25px rgba(0,0,0,.25)",
    zIndex: 9999,
  },

  loadingContainer: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    background: "#eff6ff",
  },

  loader: {
    width: "60px",
    height: "60px",
    border: "6px solid #dbeafe",
    borderTop: "6px solid #2563eb",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: "15px",
  },
};
